import { useMutation, useQuery } from '@tanstack/react-query';
import { APITypes } from '../../primary-record-types';
import urls from '../../utils/urls';
import useNotify from '../useNotify';
import { getIntl } from '../../utils/intl';
import { appAPI } from '../../utils/api';
import { onMedicalProfileUpdate, getMedicalProfilesCacheKey, onPaymentStatusOrProfileCountUpdate } from './cacheKeys';
import { useQueryErrorNotify } from '../useQueryCallbacks';

const intl = getIntl('useMedicalProfiles', {
    medicalProfilesLoadFailure: 'Failed to load Medical Profiles',
    createSuccess: 'Medical profile created successfully',
    createFailure: 'Failed to create Medical Profile',
    deleteSuccess: 'Medical profile deleted successfully',
    deleteFailure: 'Failed to delete Medical Profile',
    removeSelfAsCollaboratorSuccess: 'You have been removed as a collaborator',
    removeSelfAsCollaboratorFailure: 'Failed to remove you as a collaborator',
});

export type UseMedicalProfileArgs = {
    onCreateMedicalProfileSuccess?: (result: APITypes.medicalProfileService.CreateMedicalProfileResponse) => void;
    noCreateSuccessNotification?: boolean;
    onDeleteMedicalProfileSuccess?: () => void;
    onRemoveSelfAsCollaboratorSuccess?: () => void;
};

export type CreateEmergencyContactArgs = {
    emergencyContactDetails: APITypes.medicalProfileService.CreateEmergencyContactDetailsBody;
    medicalProfileId: string;
};

export type UseMedicalProfilesReturnValue = {
    createMedicalProfile: (body: APITypes.medicalProfileService.CreateMedicalProfileRequest) => void;
    deleteMedicalProfile: (medicalProfileId: string) => void;
    removeSelfAsCollaborator: (medicalProfileId: string) => void;
    medicalProfiles: APITypes.medicalProfileService.ProfileListItem[];
    collaboratorMedicalProfiles: APITypes.medicalProfileService.ProfileListItem[];
    isLoadingProfiles: boolean;
    isCreatingProfile: boolean;
    isDeletingProfile: boolean;
    isRemovingSelfAsCollaborator: boolean;
};

// Deletes can take a while, so just keep track of all deleted ids in memory.  This will prevent cache refreshes from still picking it up
const deletedMedicalProfileIds: string[] = [];

export default function useMedicalProfiles({
    onCreateMedicalProfileSuccess,
    noCreateSuccessNotification,
    onDeleteMedicalProfileSuccess,
    onRemoveSelfAsCollaboratorSuccess,
}: UseMedicalProfileArgs = {}): UseMedicalProfilesReturnValue {
    const notify = useNotify();
    const medicalProfilesQuery = useQuery({
        queryKey: getMedicalProfilesCacheKey(),
        queryFn: async () => {
            const withPossibleDeletions = await appAPI.get<APITypes.medicalProfileService.ListMedicalProfilesResponse>(
                urls.api.medicalProfiles.medicalProfiles,
            );
            return {
                ...withPossibleDeletions,
                collaboratorProfiles: withPossibleDeletions.collaboratorProfiles.filter(
                    (_) => !deletedMedicalProfileIds.includes(_.medicalProfileId),
                ),
                profiles: withPossibleDeletions.profiles.filter(
                    (_) => !deletedMedicalProfileIds.includes(_.medicalProfileId),
                ),
            };
        },
    });
    useQueryErrorNotify(medicalProfilesQuery, intl('medicalProfilesLoadFailure'));

    const createMedicalProfile = useMutation({
        mutationFn: async (profile: APITypes.medicalProfileService.CreateMedicalProfileRequest) => {
            const res = (await appAPI.post(urls.api.medicalProfiles.medicalProfiles, {
                body: profile,
            })) as APITypes.medicalProfileService.CreateMedicalProfileResponse;
            return res;
        },
        onSuccess: (data) => {
            !noCreateSuccessNotification && notify('success', intl('createSuccess'));
            onCreateMedicalProfileSuccess?.(data);
            onPaymentStatusOrProfileCountUpdate.resetCache({})();
            onMedicalProfileUpdate.resetCache({})();
        },
        onError: () => {
            notify('error', intl('createFailure'));
        },
    });

    const deleteMedicalProfile = useMutation({
        mutationFn: async (medicalProfileId: string) => {
            await appAPI.del(urls.api.medicalProfiles.medicalProfile(medicalProfileId));
        },
        onSuccess: (_data, medicalProfileId) => {
            notify('success', intl('deleteSuccess'));
            onDeleteMedicalProfileSuccess?.();
            onMedicalProfileUpdate.resetCache({})();
            onPaymentStatusOrProfileCountUpdate.resetCache({})();
            deletedMedicalProfileIds.push(medicalProfileId);
        },
        onError: () => {
            notify('error', intl('deleteFailure'));
        },
    });

    const removeSelfAsCollaborator = useMutation({
        mutationFn: async (medicalProfileId: string) => {
            await appAPI.del(urls.api.collaborators.me(medicalProfileId));
        },
        onSuccess: () => {
            notify('success', intl('removeSelfAsCollaboratorSuccess'));
            onRemoveSelfAsCollaboratorSuccess?.();
            onMedicalProfileUpdate.resetCache({})();
        },
        onError: () => {
            notify('error', intl('removeSelfAsCollaboratorFailure'));
        },
    });
    return {
        medicalProfiles: medicalProfilesQuery.data?.profiles || [],
        collaboratorMedicalProfiles: medicalProfilesQuery.data?.collaboratorProfiles || [],
        isLoadingProfiles: medicalProfilesQuery.isLoading,
        createMedicalProfile: createMedicalProfile.mutateAsync,
        isCreatingProfile: createMedicalProfile.isPending,
        deleteMedicalProfile: deleteMedicalProfile.mutateAsync,
        isDeletingProfile: deleteMedicalProfile.isPending,
        removeSelfAsCollaborator: removeSelfAsCollaborator.mutateAsync,
        isRemovingSelfAsCollaborator: removeSelfAsCollaborator.isPending,
    };
}
